/**
 * THE LENS RESOLVER'S TWO INPUTS, FETCHED ONCE.
 *
 * `resolvePassportWorkLenses` is pure and knows nothing about where its
 * answers come from. This hook is the only place that decides, so a second
 * Passport surface cannot quietly start asking a third source.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * INPUT A — CURRENT WORK ROLES
 * ═══════════════════════════════════════════════════════════════════════════
 * `AuthContext` already loads `membership_work_roles` onto the membership and
 * keeps the null-vs-empty distinction that everything downstream depends on:
 * `null` means NOT LOADED and falls back to the legacy scalar, `[]` means
 * somebody stated the person performs no operational work. It is read through
 * `resolveMembershipRoles` here for exactly that reason — a page-local
 * `membership.work_roles ?? []` would turn "not loaded yet" into "does
 * nothing" and the lens list would flicker from empty into correct.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * INPUT B — HISTORICAL PERFORMED ROLES
 * ═══════════════════════════════════════════════════════════════════════════
 * Counted, not listed. PostgREST has no DISTINCT, and a person with years of
 * work has tens of thousands of allocation rows; pulling them to compute a
 * three-element set would be absurd, and pulling a LIMITed page of them would
 * be worse — an old Detail Specialist stint could fall off the end and the
 * lens would silently disappear. That is the exact failure this campaign
 * exists to prevent.
 *
 * So each candidate role is asked its own exact `count` with `head: true`:
 * four tiny queries, no rows over the wire, and an answer that does not depend
 * on how much work the person has done.
 *
 * The source is `ticket_job_performer_evidence`, the canonical job-level
 * performer contract, whose `performed_work_role` is the value CAPTURED when
 * the minutes were allocated. It is `security_invoker`, so RLS applies exactly
 * as it does to the underlying allocations: the reader sees the shops they may
 * access and no others. That bound is deliberate and is NOT widened here —
 * see `MULTI-SHOP` below.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHICH TECHNICIAN ROWS ARE "THIS PERSON"
 * ═══════════════════════════════════════════════════════════════════════════
 * One professional identity can hold several shop-local `technicians` rows —
 * one per employer, historical ones included. `technician_employment_history`
 * is the join the rest of the Passport already uses (`PassportTicketLedger`,
 * `useMinuteProductivityMetrics`), and it is used here so the lens list and
 * the evidence beneath it are computed over the same set of people-in-shops.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * MULTI-SHOP
 * ═══════════════════════════════════════════════════════════════════════════
 * A person who has left a shop may no longer be able to READ that shop's
 * allocation rows; `get_accessible_shop_ids` decides, and it is the server's
 * decision to make. Where that happens the historical lens for work done there
 * will not appear. That is an omission, it is truthful, and closing it by
 * widening RLS would hand a former employee a live read on a former employer's
 * evidence tables. The honest empty state is the correct outcome and
 * `evidenceReadFailed` is carried so the UI can say "could not be loaded"
 * rather than "none".
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { resolveMembershipRoles } from '@/lib/roles/multiRole';
import {
  PASSPORT_WORK_LENSES,
  resolvePassportWorkLenses,
  type PassportWorkLens,
  type PassportWorkLensResolution,
} from '@/lib/passport/workLens';

/** One row of the canonical performer contract, in the shape read here. */
interface PerformerEvidenceRow {
  ticket_id: string;
  job_id: string;
  final_allocated_minutes: number | null;
  performed_work_role: string | null;
  performed_work_family: string | null;
  first_worked_at: string | null;
  last_worked_at: string | null;
}

export interface PassportDetailWorkEvidence {
  /** Distinct jobs recorded as DETAIL-family work. */
  readonly jobCount: number;
  /** Distinct tickets those jobs sat on. */
  readonly ticketCount: number;
  /** Allocated minutes, from the frozen allocation — not an estimate. */
  readonly minutes: number;
  readonly firstWorkedAt: string | null;
  readonly lastWorkedAt: string | null;
  /**
   * True when the row fetch hit its cap. Totals are then a FLOOR, and the UI
   * must not present them as complete.
   */
  readonly truncated: boolean;
}

const DETAIL_ROW_CAP = 1000;

export interface UsePassportWorkLensesResult {
  readonly resolution: PassportWorkLensResolution;
  readonly detailWork: PassportDetailWorkEvidence | null;
  /** The evidence read failed. Distinct from "there is no evidence". */
  readonly evidenceReadFailed: boolean;
  readonly isLoading: boolean;
}

/**
 * @param technicianProfileId the SUBJECT's professional identity. The current
 *   work roles come from the VIEWER's active membership, so this hook is for
 *   the self Passport; a cross-person surface must pass the subject's roles in
 *   itself rather than borrowing the viewer's.
 */
export function usePassportWorkLenses(
  technicianProfileId: string | null | undefined,
): UsePassportWorkLensesResult {
  const { membership, isLoading: authLoading } = useAuth();

  const enabled = !!technicianProfileId;

  const evidence = useQuery({
    queryKey: ['passport-work-lens-evidence', technicianProfileId],
    queryFn: async () => {
      const { data: employment, error: employmentError } = await supabase
        .from('technician_employment_history')
        .select('shop_technician_id')
        .eq('technician_profile_id', technicianProfileId!);
      // A read that did not happen must reach the caller as a failure, never
      // as an empty fact. CORE-C GAP-003.
      if (employmentError) throw employmentError;

      const techIds = (employment ?? [])
        .map((e) => e.shop_technician_id)
        .filter((id): id is string => !!id);

      if (techIds.length === 0) {
        return {
          performedRoles: [] as string[],
          hasUnattributedHistory: false,
          detail: null as PassportDetailWorkEvidence | null,
        };
      }

      const view = () => supabase.from('ticket_job_performer_evidence' as never);

      // One exact count per candidate role, plus one for "recorded nothing".
      // `head: true` returns no rows at all, so the cost does not grow with
      // the person's history — which is what makes the answer trustworthy.
      const roleProbes = await Promise.all(
        PASSPORT_WORK_LENSES.map(async (role) => {
          const { count, error } = await view()
            .select('ticket_id', { count: 'exact', head: true })
            .in('technician_id', techIds)
            .eq('is_performer', true)
            .eq('performed_work_role', role);
          if (error) throw error;
          return { role, count: count ?? 0 };
        }),
      );

      const { count: unattributed, error: unattributedError } = await view()
        .select('ticket_id', { count: 'exact', head: true })
        .in('technician_id', techIds)
        .eq('is_performer', true)
        .is('performed_work_role', null);
      if (unattributedError) throw unattributedError;

      // Detail rows only — the smaller set by construction, and the only one
      // whose individual values are needed.
      const { data: detailRows, error: detailError } = await view()
        .select(
          'ticket_id, job_id, final_allocated_minutes, performed_work_role, ' +
            'performed_work_family, first_worked_at, last_worked_at',
        )
        .in('technician_id', techIds)
        .eq('is_performer', true)
        .eq('performed_work_family', 'DETAIL')
        .order('last_worked_at', { ascending: false })
        .limit(DETAIL_ROW_CAP);
      if (detailError) throw detailError;

      const rows = (detailRows ?? []) as unknown as PerformerEvidenceRow[];
      const detail: PassportDetailWorkEvidence | null = rows.length
        ? {
            jobCount: new Set(rows.map((r) => `${r.ticket_id}:${r.job_id}`)).size,
            ticketCount: new Set(rows.map((r) => r.ticket_id)).size,
            minutes: rows.reduce((sum, r) => sum + (r.final_allocated_minutes ?? 0), 0),
            firstWorkedAt: rows.reduce<string | null>(
              (min, r) =>
                r.first_worked_at && (!min || r.first_worked_at < min) ? r.first_worked_at : min,
              null,
            ),
            lastWorkedAt: rows.reduce<string | null>(
              (max, r) =>
                r.last_worked_at && (!max || r.last_worked_at > max) ? r.last_worked_at : max,
              null,
            ),
            truncated: rows.length >= DETAIL_ROW_CAP,
          }
        : null;

      return {
        performedRoles: roleProbes.filter((p) => p.count > 0).map((p) => p.role as string),
        hasUnattributedHistory: (unattributed ?? 0) > 0,
        detail,
      };
    },
    enabled,
    // Historical attribution is frozen at allocation; it does not move while
    // somebody reads their own Passport.
    staleTime: 5 * 60 * 1000,
  });

  const resolution = useMemo(() => {
    const { workRoles, workRolesStated } = resolveMembershipRoles(membership as never);

    // THREE STATES, AND THE MIDDLE ONE IS EASY TO LOSE.
    //
    //   auth still settling        -> null. Not loaded.
    //   no membership at all       -> []. An ANSWER: a Passport-mode person
    //                                 with no shop has no current work roles,
    //                                 and treating that as "not loaded" would
    //                                 leave the switcher permanently hidden
    //                                 and bury any HISTORICAL lens they have
    //                                 from a previous employer.
    //   membership, child fetch    -> null, so the switcher waits rather than
    //   not yet landed                rendering a half-answer that reads as a
    //                                 role appearing a moment later.
    const currentWorkRoles = authLoading
      ? null
      : !membership
        ? []
        : (membership as { work_roles?: unknown }).work_roles != null
          ? workRoles
          : null;

    return resolvePassportWorkLenses({
      currentWorkRoles,
      // Nobody has stated anything about a person with no membership, which
      // is not the same as somebody having stated "no operational work".
      currentWorkRolesStated: !!membership && workRolesStated,
      historicalPerformedRoles: enabled
        ? (evidence.data?.performedRoles ?? null)
        : // No professional identity means no history to load — that is an
          // answer, not a pending read, so the resolution must settle.
          [],
      hasUnattributedHistory: !!evidence.data?.hasUnattributedHistory,
    });
  }, [membership, authLoading, evidence.data, enabled]);

  return {
    resolution,
    detailWork: evidence.data?.detail ?? null,
    evidenceReadFailed: evidence.isError,
    isLoading: enabled && evidence.isLoading,
  };
}

export type { PassportWorkLens };
